import {
  Box,
  Breadcrumbs,
  Link as MUILink,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Link from 'next/link';
import { FunctionComponent, useState } from 'react';

import { api } from '../../redux/services/browse/browse.generated';
import { api as cpapi } from '../../redux/services/customPlaylist/customPlaylist.generated';
import { BrowseCategoryType } from '../../redux/types.defined';

import BrowseColour from '/public/icons/layout/browse-colour.svg';
import FavouritesGrayscale from '/public/icons/layout/favourites-grayscale.svg';
import PraharGrayscale from '/public/icons/layout/prahar-grayscale.svg';
import { ChevronRight, FiberManualRecord } from '@mui/icons-material';

import { useSelector } from 'react-redux';
import {
  playingStateSelector,
  playerModeSelector,
} from '../../redux/slices/playerSlice';
import { PlayingStateEnum } from '../../enums/PlayerEnums';
import { PlayingMode } from '../../redux/types.generated';
import PraharUnselected from '/public/icons/nav-icons/prahar-unselected.svg';
import FavUnselected from '/public/icons/nav-icons/fav-unselected.svg';
import BrowseSelected from '/public/icons/nav-icons/browse-selected.svg';

import Image from 'next/image';
import PraharUnselectedPlayingGif from '/public/icons/nav-icons/prahar-unselected-playing.png';
import BrowseSelectedPlayingGif from '/public/icons/nav-icons/browse-selected-playing.png';
import FavUnselectedPlayingGif from '/public/icons/nav-icons/fav-unselected-playing.png';

interface BreadcrumbProps {
  path: string;
}

const Breadcrumb: FunctionComponent<BreadcrumbProps> = ({ path }) => {
  const pathArray = path.split('/');

  let itemId = 0;

  if (pathArray.at(3)) {
    itemId = parseInt(pathArray.at(3) as string);
  }

  const { data: artistData } = api.endpoints.artist.useQuery(
    { id: itemId!, page: 1, perPage: 20 },
    { skip: !itemId || pathArray.at(2) !== BrowseCategoryType.artistes }
  );
  const { data: instrumentData } = api.endpoints.instrument.useQuery(
    { id: itemId!, page: 1, perPage: 20 },
    { skip: !itemId || pathArray.at(2) !== BrowseCategoryType.instruments }
  );
  const { data: ragaData } = api.endpoints.raga.useQuery(
    { id: itemId!, page: 1, perPage: 20 },
    { skip: !itemId || pathArray.at(2) !== BrowseCategoryType.ragas }
  );
  const { data: playlistData } = cpapi.endpoints.customPlaylist.useQuery(
    { id: itemId!, page: 1, perPage: 20 },
    { skip: !itemId || pathArray.at(2) !== BrowseCategoryType.playlists }
  );
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm')
  );

  const getCategoryItem = () => {
    if (
      pathArray.at(2) &&
      BrowseCategoryType[pathArray.at(2) as BrowseCategoryType]
    ) {
      switch (pathArray.at(2) as BrowseCategoryType) {
        case BrowseCategoryType.artistes:
          // const artistState = api.endpoints.artist.useQueryState(queryArgs);
          return `${artistData?.artist?.firstName ?? ''} ${
            artistData?.artist?.lastName ?? ''
          }`;
        case BrowseCategoryType.instruments:
          // const instrumentState =
          //   api.endpoints.instrument.useQueryState(queryArgs);
          return instrumentData?.instrument?.name;
        case BrowseCategoryType.ragas:
          // const ragaState = api.endpoints.raga.useQueryState(queryArgs);
          return ragaData?.raga?.title;
        case BrowseCategoryType.playlists:
          return playlistData?.customPlaylist?.name;
        default:
          return '';
      }
    }
  };

  const hasUmbrellaPage = (path?: string) => {
    if (path === 'playlists') return false;
    return true;
  };

  const playingState = useSelector(playingStateSelector);
  const playerMode = useSelector(playerModeSelector);

  return (
    <>
      {!isMobile && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Link href={'/prahar'} passHref>
            <MUILink
              sx={{
                px: 1,
                mt: 1,
                transition: '0.2s ease-out',
                '&:hover': {
                  opacity: '0.75',
                },
              }}
            >
              {playingState === PlayingStateEnum.Play &&
              playerMode === PlayingMode.Prahar ? (
                <Image
                  src={PraharUnselectedPlayingGif}
                  alt='PraharSelectedPlayingGif'
                  width={37}
                  height={36}
                />
              ) : (
                <PraharUnselected />
              )}
            </MUILink>
          </Link>
          <Link href={'/favourites'} passHref>
            <MUILink
              sx={{
                px: 1,
                mt: 1,
                transition: '0.2s ease-out',
                '&:hover': {
                  opacity: '0.75',
                },
              }}
            >
              {playingState === PlayingStateEnum.Play &&
              playerMode === PlayingMode.Favourite ? (
                <Image
                  src={FavUnselectedPlayingGif}
                  alt='FavSelectedPlayingGif'
                  width={37}
                  height={36}
                />
              ) : (
                <FavUnselected />
              )}
            </MUILink>
          </Link>
        </Box>
      )}
      <Breadcrumbs
        sx={{
          '& .MuiBreadcrumbs-separator': {
            m: 0,
          },
        }}
        separator={
          <ChevronRight
            sx={{
              color: (theme) => theme.ragyaPalette.gray9,
            }}
            fontSize={isMobile ? 'small' : 'medium'}
          />
        }
      >
        <Link href={'/browse'} passHref>
          <MUILink
            sx={{
              pl: { xs: 'auto', md: 1 },
              display: 'inline-block',
              mt: 1,
              transition: '0.2s ease-out',
              '&:hover': {
                opacity: '0.75',
              },
            }}
          >
            {playingState === PlayingStateEnum.Play &&
            playerMode === PlayingMode.Playlist ? (
              <Image
                src={BrowseSelectedPlayingGif}
                alt='BrowseSelectedPlayingGif'
                width={37}
                height={36}
              />
            ) : (
              <BrowseSelected />
            )}
          </MUILink>
        </Link>
        {pathArray.at(3) && (
          <Link href={`/browse/${pathArray.at(2)}/${pathArray.at(3)}`} passHref>
            <MUILink
              underline={'none'}
              noWrap
              sx={{
                textTransform: 'capitalize',
                color: '#D3E4E4',
                fontWeight: 500,
                maxWidth: isMobile ? '150px' : 'fit-content',
                display: 'inline-block',
                mt: 1,
                transition: '0.2s ease-out',
                '&:hover': {
                  opacity: '0.75',
                },
              }}
            >
              {getCategoryItem()}
            </MUILink>
          </Link>
        )}
      </Breadcrumbs>
    </>
  );
};

export default Breadcrumb;
